// ===================================
// API SERVICE - HTTP Client
// Centralized API communication for GoPeruNow
// ===================================

class ApiService {
  constructor() {
    this.baseUrl = this.getApiBaseUrl();
    this.timeout = 30000; // 30 seconds
  }

  /**
   * Get API base URL based on environment
   */
  getApiBaseUrl() {
    const hostname = window.location.hostname;

    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "http://localhost:8000";
    } else if (hostname.includes("goperunow.com")) {
      return "https://goperunow.com";
    } else {
      return "http://localhost:8000"; // fallback
    }
  }

  /**
   * Make HTTP request with timeout and error handling
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    const defaultOptions = {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      ...options,
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...defaultOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === "AbortError") {
        throw new Error(
          "Request timeout. Please check your connection and try again."
        );
      }

      throw error;
    }
  }

  /**
   * Create booking
   */
  async createBooking(bookingData) {
    return this.request("/api/book", {
      method: "POST",
      body: JSON.stringify(bookingData),
    });
  }

  /**
   * Create a booking paid by card. `bookingData` must include a Culqi
   * token (produced client-side by Culqi Checkout) plus `amount` and
   * `currency_code` — never raw card details.
   */
  async createBookingWithPayment(bookingData) {
    return this.request("/api/book-with-payment", {
      method: "POST",
      body: JSON.stringify(bookingData),
    });
  }

  /**
   * Get tours
   */
  async getTours() {
    return this.request("/api/tours");
  }

  /**
   * Get tour by ID
   */
  async getTour(tourId) {
    return this.request(`/api/tours/${tourId}`);
  }

  /**
   * Search tours
   */
  async searchTours(filters = {}) {
    const params = new URLSearchParams(filters);
    return this.request(`/api/search?${params}`);
  }

  /**
   * Get booking status
   */
  async getBookingStatus(bookingId) {
    return this.request(`/api/booking/${bookingId}`);
  }

  /**
   * Get categories
   */
  async getCategories() {
    return this.request("/api/categories");
  }

  /**
   * Get destinations
   */
  async getDestinations() {
    return this.request("/api/destinations");
  }

  /**
   * Get featured tours
   */
  async getFeaturedTours() {
    return this.request("/api/featured");
  }

  /**
   * Health check
   */
  async healthCheck() {
    return this.request("/health");
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
